/**
 * @fileoverview Auth module for token and authentication management.
 */

import { postToHost } from "../../core/messenger";
import { AUTH_EVENTS } from "./events";
import type {
  AuthModule,
  StateGetter,
  IntrospectOptions,
  IntrospectResponse,
} from "./types";
import { introspect as introspectFunction } from "./functions/introspect";

export type {
  AuthModule,
  IntrospectOptions,
  IntrospectResponse,
} from "./types";

/**
 * Create the auth module.
 *
 * @param _getState - Function to get current SDK state (kept for consistency)
 * @returns Auth module instance
 */
export function createAuthModule(_getState: StateGetter): AuthModule {
  return {
    /**
     * Get the token from the URL query parameter.
     * The token is passed to the iframe via ?token=XXX
     *
     * @example
     * ```typescript
     * const token = embedded.auth.getToken();
     * if (token) {
     *   await verifyWithBackend(token);
     * }
     * ```
     */
    getToken(): string | null {
      const params = new URLSearchParams(window.location.search);
      return params.get("token");
    },

    /**
     * Get the app ID from the URL query parameter.
     * The app ID is passed to the iframe via ?app_id=XXX
     */
    getAppId(): string | null {
      const params = new URLSearchParams(window.location.search);
      return params.get("app_id");
    },

    /**
     * Request a token refresh from the host.
     * This will re-render the iframe with a new token URL.
     *
     * @example
     * ```typescript
     * // When token is about to expire
     * embedded.auth.refresh();
     * ```
     */
    refresh(): void {
      postToHost(AUTH_EVENTS.REFRESH, {});
    },

    /**
     * Introspect (verify) a short-lived token with Salla's API.
     * This method verifies the token and returns token information.
     *
     * @param options - Optional parameters (appId, token, and refreshOnError). If not provided, will be extracted from URL params.
     * @returns Promise that resolves with the introspect response. On API error, resolves with isVerified: false, isError: true, and empty data.
     */
    async introspect(
      options: IntrospectOptions = {},
    ): Promise<IntrospectResponse> {
      const token = options.token ?? this.getToken() ?? "";
      const appId = options.appId ?? this.getAppId() ?? "";
      return introspectFunction({
        token,
        appId,
        refreshOnError: options.refreshOnError,
      });
    },
  };
}
